import React, { useEffect } from 'react';
import { useSound } from '../contexts/SoundContext';
import { playCue } from '../lib/foley';
import type { CardSetType } from '../types/game';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentCardSet: CardSetType;
  onChangeCardSet: (cardSet: CardSetType) => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose,
  currentCardSet,
  onChangeCardSet,
}) => {
  const { soundEnabled, setSoundEnabled } = useSound();

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const handleOverlayClick = () => {
    onClose();
  };

  const handleModalClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const handleCardSetChange = (cardSet: CardSetType) => {
    if (cardSet !== currentCardSet) {
      onChangeCardSet(cardSet);
    }
  };

  const handleSoundChange = (enabled: boolean) => {
    setSoundEnabled(enabled);
    // Turning sound on is the one setting that can demonstrate itself.
    if (enabled) {
      playCue('on');
    }
  };

  return (
    <div className="modal-overlay" role="presentation" onClick={handleOverlayClick}>
      <div className="modal" onClick={handleModalClick}>
        <div className="modal__header">
          <h2>Settings</h2>
          <button
            type="button"
            className="modal__close"
            onClick={onClose}
            aria-label="Close settings"
            data-foley-click="drop"
          >
            ×
          </button>
        </div>
        <div className="modal__content">
          <div className="settings-modal__section">
            <label className="modal__label">Card Set</label>
            <p className="settings-modal__hint">
              Changing the card set will reset the current round for all players.
            </p>
            <div className="settings-modal__options">
              <button
                type="button"
                className={`settings-modal__option${currentCardSet === 'tshirt' ? ' settings-modal__option--selected' : ''}`}
                onClick={() => handleCardSetChange('tshirt')}
                data-foley-press="press"
              >
                <span className="settings-modal__option-title">T-Shirt Sizes</span>
                <span className="settings-modal__option-preview">XS, S, M, L, XL</span>
              </button>
              <button
                type="button"
                className={`settings-modal__option${currentCardSet === 'fibonacci' ? ' settings-modal__option--selected' : ''}`}
                onClick={() => handleCardSetChange('fibonacci')}
                data-foley-press="press"
              >
                <span className="settings-modal__option-title">Fibonacci</span>
                <span className="settings-modal__option-preview">1, 2, 3, 5, 8, 13, 21</span>
              </button>
            </div>
          </div>

          <div className="settings-modal__section">
            <label className="modal__label">Sound</label>
            <p className="settings-modal__hint">
              Only you hear this — it will not change anything for your teammates.
            </p>
            <div className="settings-modal__options">
              <button
                type="button"
                className={`settings-modal__option${soundEnabled ? ' settings-modal__option--selected' : ''}`}
                onClick={() => handleSoundChange(true)}
                aria-pressed={soundEnabled}
              >
                <span className="settings-modal__option-title">Sound On</span>
                <span className="settings-modal__option-preview">Card taps, votes landing, the reveal</span>
              </button>
              <button
                type="button"
                className={`settings-modal__option${!soundEnabled ? ' settings-modal__option--selected' : ''}`}
                onClick={() => handleSoundChange(false)}
                aria-pressed={!soundEnabled}
              >
                <span className="settings-modal__option-title">Muted</span>
                <span className="settings-modal__option-preview">A silent table</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
